import { useEffect, useReducer, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImgFallback from "@/components/ImgFallback";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ShieldWarning, Clock, MapPin, CreditCard,
  WarningCircle, ArrowClockwise, CheckCircle, Sparkle, LockKey,
} from "@phosphor-icons/react";

const STEP_LABELS = ["Service", "Time", "Review", "Payment"];

function reducer(state, action) {
  switch (action.type) {
    case "SET_SERVICE": return { ...state, service_id: action.id };
    case "SET_TIME": return { ...state, start_time: action.iso };
    case "SET_NOTE": return { ...state, note: action.note };
    case "SET_REF": return { ...state, reference_image: action.ref };
    case "SET_STEP": return { ...state, step: Math.max(0, Math.min(STEP_LABELS.length - 1, action.step)) };
    case "SET_PAYMENT": return { ...state, payment_state: action.state };
    case "SET_BOOKING": return { ...state, booking: action.booking, step: 99 };
    case "SET_DEMO_MODE": return { ...state, demo_payment_mode: action.mode };
    default: return state;
  }
}

const initial = {
  step: 0,
  service_id: null,
  start_time: null,
  note: "",
  reference_image: null,
  payment_state: "idle", // idle | processing | failed | ok
  booking: null,
  demo_payment_mode: "success",
};

function formatSlot(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "long", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
      {STEP_LABELS.map((label, i) => (
        <div key={label} className="flex items-center gap-2 flex-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${i <= step ? "bg-gold text-ink" : "bg-cream/10 text-cream/50"}`}>{i + 1}</div>
          <span className={`text-[10px] uppercase tracking-[0.2em] ${i === step ? "text-gold" : "text-cream/50"}`}>{label}</span>
          {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-gold" : "bg-cream/10"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function BookingWizard() {
  const { studioId } = useParams();
  const [qp] = useSearchParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, dispatch] = useReducer(reducer, { ...initial, service_id: qp.get("service") || null });

  useEffect(() => {
    api.get(`/studios/${studioId}`).then((r) => {
      setStudio(r.data);
      if (!qp.get("service") && r.data.services?.length) {
        dispatch({ type: "SET_SERVICE", id: r.data.services[0].id });
      }
      if (!state.start_time && r.data.availability?.length) {
        dispatch({ type: "SET_TIME", iso: r.data.availability[0] });
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioId]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="wizard-signin-gate">
        <p className="overline mb-3">Sign in required</p>
        <h1 className="font-display text-3xl">Sign in to complete a booking.</h1>
        <button onClick={() => nav(`/auth?next=/booking/new/${studioId}`)} className="btn-gold mt-8 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold" data-testid="wizard-signin-btn">Sign in</button>
      </div>
    );
  }

  if (loading || !studio) return <div className="max-w-3xl mx-auto px-5 py-10 text-cream/50" data-testid="wizard-loading">Loading…</div>;

  // Verification gate
  if (studio.requires_verified_member && !user.verified) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16" data-testid="verification-gate">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-cream/70 hover:text-gold mb-6" data-testid="verification-back">
          <ArrowLeft size={16} /> <span className="text-xs uppercase tracking-[0.2em]">Back</span>
        </button>
        <div className="border border-gold/40 p-8 bg-gold/5">
          <ShieldWarning size={40} color="#C5A059" weight="fill" />
          <p className="overline mt-4">Verification required</p>
          <h1 className="font-display text-3xl mt-2">{studio.name} accepts verified Members only.</h1>
          <p className="text-cream/75 mt-4 leading-relaxed">
            Some Studios choose to accept bookings only from Members who have completed identity verification.
            Complete verification in <span className="text-gold">My KUTZ</span> to book here.
          </p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mt-8">Member verification ships with Stage 5.</p>
        </div>
      </div>
    );
  }

  const service = studio.services.find((s) => s.id === state.service_id);
  const from = qp.get("look");

  // -------- confirmed step (99) --------
  if (state.step === 99 && state.booking) {
    return <BookingConfirmed booking={state.booking} studio={studio} onGo={() => nav("/upcoming")} />;
  }

  const canGoNext =
    (state.step === 0 && state.service_id) ||
    (state.step === 1 && state.start_time) ||
    (state.step === 2) ||
    (state.step === 3);

  const goNext = () => dispatch({ type: "SET_STEP", step: state.step + 1 });
  const goBack = () => state.step === 0 ? nav(-1) : dispatch({ type: "SET_STEP", step: state.step - 1 });

  const submitBooking = async () => {
    dispatch({ type: "SET_PAYMENT", state: "processing" });
    await new Promise((r) => setTimeout(r, 900));
    if (state.demo_payment_mode === "failure") {
      dispatch({ type: "SET_PAYMENT", state: "failed" });
      return;
    }
    try {
      const { data } = await api.post("/bookings", {
        studio_id: studio.id,
        service_id: service.id,
        start_time: state.start_time,
        notes: state.note,
      });
      dispatch({ type: "SET_BOOKING", booking: data });
      dispatch({ type: "SET_PAYMENT", state: "ok" });
    } catch (e) {
      dispatch({ type: "SET_PAYMENT", state: "failed" });
      toast.error(formatApiError(e.response?.data?.detail) || "Booking failed");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-32" data-testid="booking-wizard">
      <button onClick={goBack} className="flex items-center gap-2 text-cream/70 hover:text-gold mb-6" data-testid="wizard-back">
        <ArrowLeft size={16} /> <span className="text-xs uppercase tracking-[0.2em]">{state.step === 0 ? "Back" : STEP_LABELS[state.step - 1]}</span>
      </button>

      <p className="overline mb-2">Book at {studio.name}</p>
      <h1 className="font-display text-3xl md:text-5xl tracking-tighter">Book {service?.name || "a service"}</h1>

      <div className="mt-10"><Stepper step={state.step} /></div>

      {state.step === 0 && (
        <StepService studio={studio} state={state} dispatch={dispatch} />
      )}
      {state.step === 1 && (
        <StepTime studio={studio} state={state} dispatch={dispatch} />
      )}
      {state.step === 2 && (
        <StepReview studio={studio} service={service} state={state} dispatch={dispatch} fromLook={from} />
      )}
      {state.step === 3 && (
        <StepPayment
          studio={studio} service={service} state={state}
          dispatch={dispatch} onPay={submitBooking}
        />
      )}

      {state.step < 3 && (
        <div className="mt-10 flex justify-between items-center">
          <p className="text-xs text-cream/50">Step {state.step + 1} of {STEP_LABELS.length}</p>
          <button
            data-testid="wizard-next"
            disabled={!canGoNext}
            onClick={goNext}
            className="btn-gold px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
          >
            Continue <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

function StepService({ studio, state, dispatch }) {
  return (
    <div className="space-y-3" data-testid="step-service">
      <p className="overline mb-2">Choose your service</p>
      {studio.services.map((sv) => (
        <button
          key={sv.id}
          onClick={() => dispatch({ type: "SET_SERVICE", id: sv.id })}
          data-testid={`wizard-service-${sv.id}`}
          className={`w-full text-left p-5 border transition-colors flex items-start justify-between gap-4 ${state.service_id === sv.id ? "border-gold bg-cream/[0.02]" : "border-cream/10 hover:border-cream/30"}`}
        >
          <div>
            <p className="font-display text-xl tracking-tight text-cream">{sv.name}</p>
            <p className="text-xs text-cream/60 mt-1 flex items-center gap-1"><Clock size={12} /> {sv.duration_minutes} min</p>
            <p className="text-sm text-cream/70 mt-2 max-w-lg">{sv.description}</p>
          </div>
          <p className="font-display text-2xl text-gold whitespace-nowrap">${sv.price}</p>
        </button>
      ))}
    </div>
  );
}

function StepTime({ studio, state, dispatch }) {
  const [showRequest, setShowRequest] = useState(false);
  const slots = studio.availability || [];
  return (
    <div data-testid="step-time">
      <p className="overline mb-3">Choose a time</p>
      <p className="text-xs text-cream/60 mb-5">{studio.professional_name}'s real openings.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {slots.map((iso) => (
          <button
            key={iso}
            data-testid={`wizard-slot-${iso}`}
            onClick={() => dispatch({ type: "SET_TIME", iso })}
            className={`text-left p-4 border transition-colors ${state.start_time === iso ? "border-gold bg-cream/[0.02] text-gold" : "border-cream/10 hover:border-cream/30 text-cream"}`}
          >
            <p className="font-display text-lg tracking-tight">{formatSlot(iso)}</p>
          </button>
        ))}
      </div>
      <div className="mt-8 border-t border-cream/10 pt-6">
        <button
          onClick={() => setShowRequest(!showRequest)}
          data-testid="wizard-request-toggle"
          className="text-sm text-gold underline"
        >
          None of these work — Request Another Time / After Hours
        </button>
        {showRequest && (
          <div className="mt-4 border border-gold/30 p-5" data-testid="wizard-request-form">
            <p className="text-xs text-cream/70 leading-relaxed">
              Request-to-Book and After-Hours flow ships with Stage 6. For now, this Studio (Steve) is Instant Book only — pick a live slot above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepReview({ studio, service, state, dispatch, fromLook }) {
  const start = state.start_time ? new Date(state.start_time) : null;
  return (
    <div className="space-y-6" data-testid="step-review">
      <p className="overline">Review your booking</p>

      <div className="border border-cream/10 divide-y divide-cream/10">
        <Row label="Studio" value={studio.name} />
        <Row label="Professional" value={studio.professional_name} />
        <Row label="Approximate area" value={`${studio.area}, ${studio.city} · ${studio.distance_km} km`} />
        <Row label="Service" value={service?.name} />
        <Row label="Duration" value={`${service?.duration_minutes} min`} />
        <Row label="Date & time" value={start ? formatSlot(state.start_time) : "—"} />
        <Row label="Price" value={`$${service?.price}`} highlight />
      </div>

      <div className="border border-cream/10 p-5" data-testid="review-cancellation">
        <p className="overline mb-2">Cancellation summary</p>
        <p className="text-sm text-cream/80 leading-relaxed">{studio.cancellation_policy}</p>
      </div>

      <div className="border border-cream/10 p-5" data-testid="review-studio-notes">
        <p className="overline mb-2">Notes from the Studio</p>
        <p className="text-sm text-cream/80 leading-relaxed">{studio.studio_notes}</p>
      </div>

      <div>
        <p className="overline mb-2">Your note to the Professional (optional)</p>
        <textarea
          data-testid="review-member-note"
          value={state.note}
          onChange={(e) => dispatch({ type: "SET_NOTE", note: e.target.value })}
          rows={3}
          placeholder="e.g. Number 1 on the sides, scissor on top."
          className="w-full bg-transparent border border-cream/15 p-3 text-cream placeholder:text-cream/40 focus:border-gold outline-none"
        />
      </div>

      <div>
        <p className="overline mb-2">Reference image (optional)</p>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) dispatch({ type: "SET_REF", ref: URL.createObjectURL(f) });
          }}
          data-testid="review-ref-image"
          className="text-xs text-cream/60"
        />
        {state.reference_image && (
          <img src={state.reference_image} alt="reference" className="mt-3 w-28 h-28 object-cover rounded-sm border border-cream/15" />
        )}
        {fromLook && !state.reference_image && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40 mt-2">Look preselected from Discover · {fromLook}</p>
        )}
      </div>

      <div className="text-xs text-cream/50 border-t border-cream/10 pt-4">
        Member price matches the advertised price. No additional KUTZ Member booking fee.
      </div>
    </div>
  );
}

function StepPayment({ studio, service, state, dispatch, onPay }) {
  const [form, setForm] = useState({ name: "", number: "", expiry: "", cvc: "" });
  const canPay = form.name && form.number.length >= 12 && form.expiry && form.cvc.length >= 3 && state.payment_state !== "processing";
  return (
    <div data-testid="step-payment">
      <p className="overline">Mock payment</p>
      <h2 className="font-display text-2xl mt-1">Pay ${service?.price} to confirm.</h2>
      <p className="text-xs text-cream/50 mt-1">No real card processed. This is a demo screen — Stripe wires up post-Stage 8.</p>

      <div className="mt-8 border border-cream/10 p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/60">
          <LockKey size={14} color="#C5A059" /> Secure test checkout
        </div>
        <input
          data-testid="pay-name"
          placeholder="Name on card"
          className="input-line" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          data-testid="pay-number"
          placeholder="Card number  4242 4242 4242 4242"
          className="input-line" value={form.number}
          onChange={(e) => setForm({ ...form, number: e.target.value.replace(/[^\d ]/g, "") })}
        />
        <div className="grid grid-cols-2 gap-4">
          <input
            data-testid="pay-expiry"
            placeholder="MM/YY"
            className="input-line" value={form.expiry}
            onChange={(e) => setForm({ ...form, expiry: e.target.value })}
          />
          <input
            data-testid="pay-cvc"
            placeholder="CVC"
            className="input-line" value={form.cvc}
            onChange={(e) => setForm({ ...form, cvc: e.target.value.replace(/[^\d]/g, "") })}
          />
        </div>
      </div>

      {/* Demo mode toggle */}
      <div className="mt-5 flex items-center gap-3" data-testid="pay-demo-mode">
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40">Demo</p>
        <div className="flex gap-1 border border-cream/15 p-1 rounded-full">
          {["success", "failure"].map((m) => (
            <button
              key={m}
              data-testid={`pay-mode-${m}`}
              onClick={() => dispatch({ type: "SET_DEMO_MODE", mode: m })}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] ${state.demo_payment_mode === m ? "bg-gold text-ink font-semibold" : "text-cream/70"}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {state.payment_state === "failed" && (
        <div className="mt-6 border border-red-500/40 p-4 bg-red-500/5 flex items-center gap-3" data-testid="pay-failed">
          <WarningCircle size={20} color="#f87171" weight="fill" />
          <div className="flex-1">
            <p className="text-sm text-cream">Payment couldn't be processed.</p>
            <p className="text-xs text-cream/60">Try again — no card was charged.</p>
          </div>
          <button
            onClick={() => dispatch({ type: "SET_PAYMENT", state: "idle" })}
            data-testid="pay-retry"
            className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
          >
            <ArrowClockwise size={12} /> Retry
          </button>
        </div>
      )}

      <button
        data-testid="pay-submit"
        disabled={!canPay}
        onClick={onPay}
        className="btn-gold w-full mt-8 px-6 py-4 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"
      >
        {state.payment_state === "processing" ? "Processing…" : (
          <><CreditCard size={14} /> Pay ${service?.price} · Confirm booking</>
        )}
      </button>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between p-4">
      <span className="text-xs uppercase tracking-[0.2em] text-cream/60">{label}</span>
      <span className={highlight ? "font-display text-xl text-gold" : "text-sm text-cream"}>{value}</span>
    </div>
  );
}

function BookingConfirmed({ booking, studio, onGo }) {
  return (
    <div className="max-w-2xl mx-auto px-5 py-16 text-center" data-testid="booking-confirmed">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold text-ink mb-6">
        <CheckCircle size={36} weight="fill" />
      </div>
      <p className="overline mb-2">Confirmed</p>
      <h1 className="font-display text-5xl md:text-6xl tracking-tighter text-cream">YOU'RE BOOKED.</h1>
      <p className="text-cream/70 mt-4">
        {booking.service_name} at {studio.name} with {studio.professional_name}
      </p>
      <p className="text-xs text-cream/50 mt-2 flex items-center justify-center gap-1">
        <Clock size={12} weight="fill" color="#C5A059" />
        {new Date(booking.start_time).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" })}
      </p>

      <div className="mt-10 border border-cream/10 p-6 text-left space-y-3">
        <div className="flex items-center gap-2 text-xs text-gold">
          <Sparkle size={14} weight="fill" /> Your exact address unlocks 90 minutes before your appointment.
        </div>
        <div className="flex items-center gap-2 text-xs text-cream/70">
          <MapPin size={14} weight="fill" color="#C5A059" /> Approximately {studio.area}, {studio.city} · {studio.distance_km} km
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-3">
        <button onClick={onGo} data-testid="confirmed-view-upcoming" className="btn-gold px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">View my booking</button>
      </div>
    </div>
  );
}
